import React from 'react'
import { StopOutlined } from '@ant-design/icons'
import { Checkbox, Tooltip } from 'antd'
import { groupBy, isEmpty, isNil, some } from 'lodash'

import {
  JsonDescriptorCollapse,
  ReadMore,
  TextDescriptor,
} from '../../common/components/sections'
import {
  CERTAINTY_TYPE,
  CURATION_CERTAINTY_THRESHOLD,
} from '../../common/constants'

import MenuTitleEditableDescriptionCell from '../components/sections/menu-title-editable-description-cell'
import MenuTitleRetryLabelAndIngredientsButton from '../components/sections/menu-title-retry-label-and-ingredients-button'
import { StyledMenuTitleIgnoreButton } from '../components/styled'

const createMenuTitleTableColumns = ({
  handleSetMenuTitleForIgnoredDishType,
  isDescriptionDisabled,
  isIgnoreButtonDisabled,
  menuTitlesFlat,
  highlights,
  isMenuFake,
}) => [
  {
    title: 'Title',
    width: '500px',
    render: ({ id, styledTitle, title }) => (
      <MenuTitleEditableDescriptionCell
        menuTitleId={id}
        fieldName="title"
        value={title}
        styledValue={styledTitle}
        isDisabled={isDescriptionDisabled}
        isTextEditingEnabled={isMenuFake}
        highlightsFieldName="name"
        menuTitleHighlights={highlights?.[id]}
      />
    ),
  },
  {
    title: 'Certainty',
    dataIndex: 'certainty',
    width: '100px',
    key: 'certainty',
    render: certainty => {
      let certaintyType = CERTAINTY_TYPE.NO_CURATION_DATA

      if (!isNil(certainty)) {
        certaintyType =
          certainty < CURATION_CERTAINTY_THRESHOLD
            ? CERTAINTY_TYPE.LOW
            : CERTAINTY_TYPE.HIGH
      }

      return certaintyType
    },
  },
  {
    title: 'Description',
    width: '700px',
    render: ({ id, styledDescription, description }) => (
      <MenuTitleEditableDescriptionCell
        menuTitleId={id}
        fieldName="description"
        value={description}
        styledValue={styledDescription}
        isDisabled={isDescriptionDisabled}
        isTextEditingEnabled={isMenuFake}
        highlightsFieldName="description"
        menuTitleHighlights={highlights?.[id]}
      />
    ),
  },
  ...(some(menuTitlesFlat, ({ miscDescriptors }) => !isEmpty(miscDescriptors))
    ? [
        {
          title: 'Misc Descriptors',
          dataIndex: 'miscDescriptors',
          width: '160px',
          render: descriptors =>
            isEmpty(descriptors) ? null : (
              <ReadMore
                maxHeight={170}
                component={
                  <TextDescriptor
                    value={descriptors}
                    prefix="miscDescriptors"
                  />
                }
              />
            ),
        },
      ]
    : []),
  ...(some(menuTitlesFlat, ({ addonDescriptors }) => !isEmpty(addonDescriptors))
    ? [
        {
          title: 'Addon Descriptors',
          dataIndex: 'addonDescriptors',
          width: '160px',
          render: (_, { addonDescriptors }) =>
            isEmpty(addonDescriptors) ? null : (
              <ReadMore
                maxHeight={170}
                maxWidth="160px"
                component={
                  <TextDescriptor
                    value={addonDescriptors}
                    prefix="addonDescriptors"
                  />
                }
              />
            ),
        },
      ]
    : []),
  ...(some(menuTitlesFlat, ({ dietDescriptors }) => !isEmpty(dietDescriptors))
    ? [
        {
          title: 'Diet Descriptors',
          dataIndex: 'dietDescriptors',
          width: '170px',
          render: (_, { id, dietDescriptors }) =>
            !isEmpty(dietDescriptors) ? (
              <ReadMore
                maxWidth="170px"
                maxHeight={170}
                component={
                  <JsonDescriptorCollapse
                    collapsedByDefault
                    value={dietDescriptors}
                    highlights={groupBy(highlights?.[id], 'originField')}
                  />
                }
              />
            ) : null,
        },
      ]
    : []),
  ...(some(
    menuTitlesFlat,
    ({ allergenDescriptors }) => !isEmpty(allergenDescriptors),
  )
    ? [
        {
          title: 'Allergen Descriptors',
          width: '170px',
          dataIndex: 'allergenDescriptors',
          render: (_, { id, allergenDescriptors }) =>
            isEmpty(allergenDescriptors) ? null : (
              <ReadMore
                maxHeight={170}
                maxWidth="160px"
                component={
                  <JsonDescriptorCollapse
                    value={allergenDescriptors}
                    highlights={groupBy(highlights?.[id], 'originField')}
                    prefix="allergenDescriptors"
                  />
                }
              />
            ),
        },
      ]
    : []),
  {
    key: 'actions',
    width: '150px',
    render: (_text, record) => (
      <>
        <MenuTitleRetryLabelAndIngredientsButton
          menuTitle={record}
          isDisabled={isDescriptionDisabled || isIgnoreButtonDisabled}
        />
        {record.level !== 0 && (
          <Tooltip title="Set ignored dish type">
            <StyledMenuTitleIgnoreButton
              icon={<StopOutlined />}
              disabled={isIgnoreButtonDisabled}
              isUsed={!isNil(record.dishTypeId)}
              shape="circle"
              onClick={() => handleSetMenuTitleForIgnoredDishType(record.id)}
            />
          </Tooltip>
        )}
      </>
    ),
  },
  {
    title: 'Id',
    dataIndex: 'id',
  },
  {
    title: 'Published',
    render: (_, record) => <Checkbox checked={record.isPublished} disabled />,
  },
  {
    title: 'Autoeat Id',
    dataIndex: 'autoeatId',
  },
]

export default createMenuTitleTableColumns
